import type { Role, Action, Resource } from '@knownow/domain'

/**
 * Permission map: role → set of allowed (action, resource) pairs.
 *
 * Rules from design doc:
 * - owner:  all actions on all resources
 * - admin:  manage_members (member/viewer targets only — enforced at use-case level),
 *           CRUD on data resources, read on org resources
 * - member: CRUD on data resources, read on org resources
 * - viewer: read only
 */

type PermissionKey = `${Action}:${Resource}`

const OWNER_PERMISSIONS: readonly PermissionKey[] = [
  'manage_org:organization',
  'manage_members:member',
  'manage_members:invitation',
  'create:plan',
  'read:plan',
  'update:plan',
  'delete:plan',
  'create:conversation',
  'read:conversation',
  'update:conversation',
  'delete:conversation',
  'create:organization',
  'read:organization',
  'update:organization',
  'delete:organization',
  'create:member',
  'read:member',
  'update:member',
  'delete:member',
  'create:invitation',
  'read:invitation',
  'update:invitation',
  'delete:invitation',
]

const ADMIN_PERMISSIONS: readonly PermissionKey[] = [
  'manage_members:member',
  'manage_members:invitation',
  'create:plan',
  'read:plan',
  'update:plan',
  'delete:plan',
  'create:conversation',
  'read:conversation',
  'update:conversation',
  'delete:conversation',
  'read:organization',
  'create:member',
  'read:member',
  'update:member',
  'delete:member',
  'create:invitation',
  'read:invitation',
  'update:invitation',
  'delete:invitation',
]

const MEMBER_PERMISSIONS: readonly PermissionKey[] = [
  'create:plan',
  'read:plan',
  'update:plan',
  'delete:plan',
  'create:conversation',
  'read:conversation',
  'update:conversation',
  'delete:conversation',
  'read:organization',
  'read:member',
  'read:invitation',
]

const VIEWER_PERMISSIONS: readonly PermissionKey[] = [
  'read:plan',
  'read:conversation',
  'read:organization',
  'read:member',
  'read:invitation',
]

const PERMISSION_MAP: Record<Role, ReadonlySet<PermissionKey>> = {
  owner: new Set(OWNER_PERMISSIONS),
  admin: new Set(ADMIN_PERMISSIONS),
  member: new Set(MEMBER_PERMISSIONS),
  viewer: new Set(VIEWER_PERMISSIONS),
}

export function isAllowed(role: Role, action: Action, resource: Resource): boolean {
  const key: PermissionKey = `${action}:${resource}`
  return PERMISSION_MAP[role].has(key)
}
